
import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';

interface AuthenticatedRouteProps {
  children: React.ReactNode;
}

/**
 * A route that only requires authentication, without checking subscription status.
 * Used for pages that should be accessible to logged-in users regardless of subscription,
 * such as the league selection page.
 */
const AuthenticatedRoute: React.FC<AuthenticatedRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  // While checking authentication status, show a loading state
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center flex-col gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">
          Verifying your account...
        </p>
      </div>
    );
  }
  
  // If not authenticated, redirect to login
  if (!user) {
    console.log("User not authenticated, redirecting to login");
    return <Navigate 
      to="/login" 
      replace 
      state={{ 
        returnTo: location.pathname + location.search
      }} 
    />;
  }
  
  // If authenticated, render the protected component
  return <>{children}</>;
};

export default AuthenticatedRoute;
